import {
  canEditBoard,
  DomainError,
  newId,
  SAVE_DEBOUNCE_MS,
  type BoardAccess,
  type MindmapNode,
} from '@blueprint/domain'
import { create } from 'zustand'
import { RepositoryError } from '../data/index.js'
import { getRepository, refreshBoardList, targetProjectId, useBoardStore } from './board-store.js'
import { useUiStore } from './ui-store.js'

export type MindmapPhase = 'idle' | 'loading' | 'ready' | 'missing' | 'error'
export type MindmapSaveStatus = 'saved' | 'pending' | 'saving' | 'error' | 'conflict'

interface MindmapStore {
  phase: MindmapPhase
  id: string | null
  name: string
  access: BoardAccess
  root: MindmapNode | null
  /** The server revision the next save is made against. */
  revision: number
  saveStatus: MindmapSaveStatus
  saveError: string | null
  lastSavedAt: string | null
  /** A node to select once the map is open, e.g. when arriving from a storymap card. */
  focusNodeId: string | null
  /** Bumped by every load from the server, so the editor knows to take the tree afresh. */
  loads: number

  open: (id: string, focusNodeId?: string | null) => Promise<void>
  /** Creates and opens a mindmap; `root` is given by an import. Resolves to the new id. */
  create: (name: string, root?: MindmapNode) => Promise<string | null>
  /** Every committed edit: the whole tree, optionally with a new name. Saved debounced. */
  change: (root: MindmapNode, name?: string) => void
  /** Waits for every pending change to reach the server. */
  flush: () => Promise<void>
  retry: () => void
  /** After a conflict: drop local changes and load the server's version. */
  reload: () => Promise<void>
  leave: () => Promise<void>
  clearFocus: () => void
}

function describe(error: unknown): string {
  if (error instanceof RepositoryError || error instanceof DomainError || error instanceof Error) return error.message
  return 'Something went wrong.'
}

let timer: ReturnType<typeof setTimeout> | null = null
let inFlight: Promise<void> | null = null
/** Set by every change; cleared when that state has been sent. */
let dirty = false
/** Whether the name changed since the last save, so an unchanged name is not resent. */
let nameDirty = false

function clearTimer(): void {
  if (timer) clearTimeout(timer)
  timer = null
}

/**
 * One save at a time, always of the latest tree. Changes made while a save is in flight
 * are sent by the next pass of the loop, against the revision the first one returned.
 */
async function saveLoop(): Promise<void> {
  const store = useMindmapStore
  while (dirty) {
    const { id, root, name, revision } = store.getState()
    if (!id || !root) return
    dirty = false
    const sendName = nameDirty
    nameDirty = false
    store.setState({ saveStatus: 'saving', saveError: null })
    try {
      const saved = await getRepository().saveMindmap(id, {
        expectedRevision: revision,
        root,
        ...(sendName ? { name } : {}),
        sync: useUiStore.getState().linkSync,
      })
      if (store.getState().id !== id) return
      store.setState({ revision: saved.revision, lastSavedAt: saved.updatedAt })
      if (sendName) void refreshBoardList().catch(() => undefined)
    } catch (error) {
      if (store.getState().id !== id) return
      // The change is still unsaved: keep it marked so a retry sends it.
      dirty = true
      nameDirty ||= sendName
      const conflict = error instanceof RepositoryError && error.kind === 'conflict'
      store.setState({ saveStatus: conflict ? 'conflict' : 'error', saveError: describe(error) })
      return
    }
  }
  store.setState({ saveStatus: 'saved' })
}

function startSave(): Promise<void> {
  clearTimer()
  inFlight ??= saveLoop().finally(() => {
    inFlight = null
  })
  return inFlight
}

export const useMindmapStore = create<MindmapStore>((set, get) => ({
  phase: 'idle',
  id: null,
  name: '',
  access: 'owner',
  root: null,
  revision: 0,
  saveStatus: 'saved',
  saveError: null,
  lastSavedAt: null,
  focusNodeId: null,
  loads: 0,

  async open(id, focusNodeId = null) {
    if (get().id === id && get().phase === 'ready') {
      if (focusNodeId) set({ focusNodeId })
      return
    }
    await get().leave()
    set({ phase: 'loading', id, root: null, saveStatus: 'saved', saveError: null, focusNodeId })
    try {
      const mindmap = await getRepository().getMindmap(id)
      if (get().id !== id) return
      set({
        phase: 'ready',
        name: mindmap.name,
        access: mindmap.access,
        root: mindmap.root,
        revision: mindmap.revision,
        lastSavedAt: mindmap.updatedAt,
        loads: get().loads + 1,
      })
    } catch (error) {
      if (get().id !== id) return
      const missing = error instanceof RepositoryError && error.kind === 'not_found'
      set({ phase: missing ? 'missing' : 'error', saveError: missing ? null : describe(error) })
    }
  },

  async create(name, root) {
    try {
      const projectId = targetProjectId()
      const created = await getRepository().createMindmap({ id: newId(), name: name.trim() || 'New mindmap', root, ...(projectId ? { projectId } : {}) })
      // The project list too: its item counts changed.
      await useBoardStore.getState().loadProjects()
      await get().open(created.id)
      return created.id
    } catch (error) {
      set({ saveError: describe(error) })
      return null
    }
  },

  change(root, name) {
    if (!get().id || !canEditBoard(get().access)) return
    const renamed = name !== undefined && name !== get().name
    set({ root, ...(renamed ? { name } : {}), saveStatus: get().saveStatus === 'conflict' ? 'conflict' : 'pending' })
    dirty = true
    nameDirty ||= renamed
    // A conflict waits for the user's choice; nothing more is sent until then.
    if (get().saveStatus === 'conflict') return
    clearTimer()
    timer = setTimeout(() => void startSave(), SAVE_DEBOUNCE_MS)
  },

  async flush() {
    if (!dirty && !inFlight) return
    if (get().saveStatus === 'conflict') return
    await startSave()
    if (dirty && get().saveStatus !== 'saved') throw new Error(get().saveError ?? 'The mindmap could not be saved.')
  },

  retry() {
    if (get().saveStatus === 'error') void startSave()
  },

  async reload() {
    const id = get().id
    if (!id) return
    clearTimer()
    dirty = false
    nameDirty = false
    set({ phase: 'idle', id: null })
    await get().open(id)
  },

  async leave() {
    if (!get().id) return
    try {
      await get().flush()
    } catch {
      // Leaving is not blocked: the save status already told the user, and the
      // dashboard shows the last saved version.
    }
    clearTimer()
    dirty = false
    nameDirty = false
    set({ phase: 'idle', id: null, root: null, name: '', saveStatus: 'saved', saveError: null, focusNodeId: null })
  },

  clearFocus() {
    set({ focusNodeId: null })
  },
}))

/** Whether the open mindmap has changes the server has not acknowledged. */
export function hasUnsavedMindmap(): boolean {
  return dirty || inFlight !== null
}
